import useResizeObserver from '@react-hook/resize-observer';
import React from 'react';

import { isElementOverflowed } from '../../utils/dom/element';

interface Returns<T> {
  /**
   * Ref object to apply to the element you want to check overflow for.
   */
  ref: React.RefObject<T>;

  /**
   * A value representing whether the element referenced by the ref is overflowed.
   */
  isOverflowed: boolean;
}

/**
 * Returns a ref object and a value indicating whether the element reference by the ref is
 * overflowed.
 */
export const useElementOverflow = <T extends HTMLElement>(): Returns<T> => {
  const ref = React.useRef<T>(null);
  const [isOverflowed, setIsOverflowed] = React.useState(false);
  const prevElementRef = React.useRef<T | null>(null);

  const handleOverflow = React.useCallback((target: Element) => {
    setIsOverflowed(isElementOverflowed(target));
  }, []);

  // Check overflow when element referenced by the ref is resized.
  useResizeObserver(ref, (entry) => handleOverflow(entry.target));

  // Check overflow when ref.current changes (element attached/detached).
  // Note:
  // The Resize Observer might not be triggered when the element referenced by the ref is inside a
  // table with a fixed layout. This effect takes care of that.
  // Using useLayoutEffect to run synchronously after DOM mutations but before paint.
  React.useLayoutEffect(() => {
    const currentElement = ref.current;
    // Only check if the element actually changed
    if (currentElement && currentElement !== prevElementRef.current) {
      prevElementRef.current = currentElement;
      handleOverflow(currentElement);
    } else if (!currentElement) {
      prevElementRef.current = null;
    }
  });

  return {
    ref,
    isOverflowed,
  };
};
